import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Truck, Package, Cpu, Tablet, Smartphone, Gauge, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  truck: { label: 'Truck', icon: Truck, color: 'text-[#002FA7]', bg: 'bg-blue-50' },
  trailer: { label: 'Trailer', icon: Package, color: 'text-[#FF9500]', bg: 'bg-orange-50' },
  gps: { label: 'GPS', icon: Cpu, color: 'text-[#34C759]', bg: 'bg-green-50' },
  eld: { label: 'ELD', icon: Cpu, color: 'text-purple-600', bg: 'bg-purple-50' },
  tablet: { label: 'Tablet', icon: Tablet, color: 'text-slate-600', bg: 'bg-slate-100' },
  other: { label: 'Other', icon: Smartphone, color: 'text-slate-600', bg: 'bg-slate-100' },
};

const DriverAssets = () => {
  const [assets, setAssets] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [aRes, rRes] = await Promise.all([
          axios.get(`${API}/assets/me`, { withCredentials: true }),
          axios.get(`${API}/maintenance-rules`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setAssets(aRes.data);
        setRules(rRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dueByAsset = (assetId) => {
    return rules.filter(r => {
      if (r.asset_id !== assetId) return false;
      const odo = assets.find(a => a.id === assetId)?.current_odometer || 0;
      if (r.interval_miles) {
        const last = r.last_performed_miles || 0;
        if ((last + r.interval_miles) - odo <= 500) return true;
      }
      if (r.interval_days && r.last_performed_at) {
        const next = new Date(r.last_performed_at);
        next.setDate(next.getDate() + r.interval_days);
        if ((next - new Date()) / 86400000 <= 14) return true;
      }
      return false;
    });
  };

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="driver-assets-page">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>My Equipment</h1>
        <p className="text-base text-slate-600 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Assets assigned to you and any upcoming maintenance.</p>

        {assets.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="no-assigned-assets">
            <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No equipment assigned to you yet. Ask your dispatcher.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {assets.map(a => {
              const meta = TYPE_META[a.asset_type] || TYPE_META.other;
              const Icon = meta.icon;
              const due = dueByAsset(a.id);
              return (
                <div key={a.id} data-testid={`my-asset-${a.id}`} className="bg-white border border-slate-200 rounded-md p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${meta.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${meta.color}`} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{meta.label}</p>
                      <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{a.label}</h3>
                      <p className="text-xs text-slate-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {[a.make, a.model].filter(Boolean).join(' ')}{a.license_plate ? ` • ${a.license_plate}` : ''}
                      </p>
                      {a.asset_type === 'truck' && (
                        <div className="flex items-center gap-1 mt-2 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          <Gauge className="w-3.5 h-3.5 text-slate-500" />
                          <span className="text-slate-700">{Math.round(a.current_odometer || 0).toLocaleString()} mi</span>
                        </div>
                      )}
                      {due.length > 0 && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-amber-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Maintenance due soon</p>
                            <p className="text-xs text-amber-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{due.map(d => d.name).join(', ')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DriverAssets;
